import {Injectable, signal} from '@angular/core';
import {GrafikUmfang} from '../model/event-bus.model';
import {environment} from '../../environments/environment';
import {debounceTime, Subject, switchMap} from 'rxjs';
import {ForecastStand, StandAbfrage} from '../model/forecast-stand.model';
import {ForecastStandService} from './http/forecast-stand.service';

/** Woher die Werte einer Karte kamen. */
export type DatenquelleArt = 'mos' | 'snapshot' | 'einzelabruf';

/** Eine Datenquelle, aus der ein Knopfdruck "MSWR-MOS laden" Werte geholt hat. */
export interface Datenquelle {
  /** MOS-Datei, Snapshot des Rest-Service oder Einzelabruf bei der Wetterdaten-API. */
  art: DatenquelleArt;
  /** Name der Quelle, z.B. "01_Aachen_Vorhersage.txt". */
  name: string;
  /**
   * Von wann die Daten sind: bei der MOS-Datei ihr `Last-Modified`, beim Snapshot der
   * Zeitpunkt des Refreshs auf dem Rest-Service. `null`, wenn die Quelle keinen Stempel
   * mitliefert (Einzelabruf, oder die Wetterdaten-API schickt keinen `Last-Modified`).
   */
  stand: Date | null;
  /** Wie viele Karten des Umfangs daraus geladen haben. */
  karten: number;
}

/** Was ein Knopfdruck "MSWR-MOS laden" geladen hat, wie es der Kasten anzeigt. */
export interface LadeBericht {
  /** Beschriftung des Umfangs (Reiter- bzw. Zeilenname). */
  name: string;
  /** Wann der Knopf gedrückt wurde. */
  gestartet: Date;
  /** Die Quellen in der Reihenfolge ihrer ersten Meldung. */
  quellen: Datenquelle[];
  /**
   * Stand der Vorhersagepakete, die dieser Umfang benutzt hat (`POST forecast/stand`): je
   * Sammlung der Wetterdaten-API die Zahl der Stationen und die `updated`-Zeitstempel ihrer
   * Einträge im Cache des Rest-Service. `undefined` = noch nicht geantwortet, `null` = nicht
   * abrufbar.
   */
  stand?: ForecastStand | null;
  /** Die gefüllten Boxen (Reihe + Ort), aus denen der Stand ermittelt wird – intern. */
  abfragen: StandAbfrage[];
}

/** Pause nach der letzten Box-Meldung, bevor der Stand abgefragt wird (ms). */
export const STAND_BUENDEL_MS = 600;

/**
 * Die Datenquellen des letzten "MSWR-MOS laden" – **ein Bericht je Umfang**, damit der
 * Redakteur sieht, was genau geladen wurde und von wann die Daten sind.
 *
 * Dasselbe Muster wie {@link KiErgebnisService}: der Knopf steht oben für den Reiter
 * und an jeder Zeile für deren Karten, der Kasten gehört neben den gedrückten Knopf.
 * Geschlüsselt über {@link GrafikUmfang#schluessel}, die Map liegt in einem Signal, weil
 * die Meldungen aus den HTTP-Callbacks der über 200 Bild-Komponenten eintreffen – also
 * außerhalb jeder Bindung –, und Auslöser und Anzeige Geschwister-Ansichten sind.
 *
 * Gemeldet wird je Karte, zusammengeführt wird hier: dieselbe Quelle (Art + Name) zählt
 * einmal, mit der Zahl der Karten, die aus ihr geladen haben. Ein Druck auf "Heute" trifft
 * sechs Zeilen à drei Karten, die sich eine MOS-Datei und einen Snapshot teilen – der
 * Kasten soll zwei Zeilen zeigen, nicht achtzehn.
 *
 * Abschaltbar über `environment.showLadeQuellen`: dann nimmt der Service keine Berichte an,
 * die Kästen bleiben leer und erscheinen nicht – das Laden selbst ist davon unberührt.
 */
@Injectable({providedIn: 'root'})
export class LadeQuellenService {

  private readonly berichte = signal<ReadonlyMap<string, LadeBericht>>(new Map());
  /** Je Umfang ein Auslöser für die gebündelte Stand-Abfrage (s. {@link starte}). */
  private readonly ausloeser = new Map<string, Subject<void>>();

  constructor(private forecastStandService: ForecastStandService) {
  }

  /** Ist der Kasten per Konfiguration eingeschaltet? */
  static get eingeschaltet(): boolean {
    return environment.showLadeQuellen !== false;
  }

  /** Der Bericht für diesen Umfang, oder null solange keiner vorliegt (oder abgeschaltet). */
  berichtFuer(umfang: GrafikUmfang | null | undefined): LadeBericht | null {
    if (!LadeQuellenService.eingeschaltet || !umfang) {
      return null;
    }
    return this.berichte().get(umfang.schluessel) ?? null;
  }

  /**
   * Ein Knopfdruck beginnt: alten Bericht dieses Umfangs verwerfen und einen leeren mit
   * Startzeit anlegen – der Kasten erscheint sofort und füllt sich, sobald die Karten melden.
   */
  starte(umfang: GrafikUmfang | null | undefined): void {
    if (!LadeQuellenService.eingeschaltet || !umfang) {
      return;
    }
    const bericht: LadeBericht = {name: umfang.name, gestartet: new Date(), quellen: [], abfragen: []};
    this.setze(umfang, bericht);
    // Der Stand der Vorhersagepakete wird einmal je Knopfdruck abgefragt – aber erst, wenn die
    // Karten gemeldet haben, welche Boxen sie gefüllt haben (merkeAbfrage). Die Meldungen
    // treffen gestaffelt aus den HTTP-Callbacks ein, deshalb gebündelt: STAND_BUENDEL_MS nach
    // der letzten Meldung, mindestens einmal (auch wenn keine Box über die Stationen lief).
    // Die Antwort gehört nur zu diesem Bericht: hat inzwischen ein neuer Druck einen neuen
    // angelegt, verfällt sie (gestartet ist die Identität des Berichts).
    this.ausloeser.get(umfang.schluessel)?.complete();
    const trigger = new Subject<void>();
    this.ausloeser.set(umfang.schluessel, trigger);
    trigger.pipe(
      debounceTime(STAND_BUENDEL_MS),
      switchMap(() => this.forecastStandService.lade(this.berichtFuer(umfang)?.abfragen ?? []))
    ).subscribe(stand => {
      const aktuell = this.berichtFuer(umfang);
      if (aktuell && aktuell.gestartet === bericht.gestartet) {
        this.setze(umfang, {...aktuell, stand});
      }
    });
    trigger.next();
  }

  /**
   * Eine Karte meldet eine Box, die sie über die Stationen gefüllt hat (Reihe + Region bzw.
   * Station). Daraus bestimmt der Rest-Service die benutzten Pakete und Stationen; gleiche
   * Meldungen zählen einmal. Ohne Bericht (kein starte, kleiner Knopf an der Karte) nichts.
   */
  merkeAbfrage(umfang: GrafikUmfang | null | undefined, abfrage: StandAbfrage): void {
    if (!LadeQuellenService.eingeschaltet || !umfang) {
      return;
    }
    const bisher = this.berichtFuer(umfang);
    if (!bisher) {
      return;
    }
    const doppelt = bisher.abfragen.some(a => a.reihe === abfrage.reihe && a.region === abfrage.region
      && a.station === abfrage.station && a.wind === abfrage.wind && a.temperatur === abfrage.temperatur);
    if (!doppelt) {
      this.setze(umfang, {...bisher, abfragen: [...bisher.abfragen, abfrage]});
    }
    this.ausloeser.get(umfang.schluessel)?.next();
  }

  /**
   * Eine Karte meldet, woraus sie geladen hat. Ohne Umfang (kleiner Knopf an der Karte)
   * passiert nichts – dafür gibt es keinen Kasten. Ohne vorherigen {@link starte} wird der
   * Bericht angelegt, damit keine Meldung verloren geht.
   */
  melde(umfang: GrafikUmfang | null | undefined, quelle: Omit<Datenquelle, 'karten'>): void {
    if (!LadeQuellenService.eingeschaltet || !umfang) {
      return;
    }
    const bisher = this.berichtFuer(umfang) ?? {name: umfang.name, gestartet: new Date(), quellen: [], abfragen: []};
    const quellen = bisher.quellen.map(q => ({...q}));
    const vorhanden = quellen.find(q => q.art === quelle.art && q.name === quelle.name);
    if (vorhanden) {
      vorhanden.karten += 1;
      vorhanden.stand = vorhanden.stand ?? quelle.stand;
    } else {
      quellen.push({...quelle, karten: 1});
    }
    this.setze(umfang, {...bisher, quellen});
  }

  /** Den Kasten eines Umfangs ausblenden (Schließen-Knopf). */
  schliessen(umfang: GrafikUmfang | null | undefined): void {
    if (!umfang) {
      return;
    }
    this.ausloeser.get(umfang.schluessel)?.complete();
    this.ausloeser.delete(umfang.schluessel);
    const naechste = new Map(this.berichte());
    if (naechste.delete(umfang.schluessel)) {
      this.berichte.set(naechste);
    }
  }

  /** Alle Berichte verwerfen – beim Laden eines Dokuments, der Service überdauert die Seite. */
  alleLeeren(): void {
    this.ausloeser.forEach(t => t.complete());
    this.ausloeser.clear();
    this.berichte.set(new Map());
  }

  private setze(umfang: GrafikUmfang, bericht: LadeBericht): void {
    const naechste = new Map(this.berichte());
    naechste.set(umfang.schluessel, bericht);
    this.berichte.set(naechste);
  }
}
